import { Alert, Space } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Link from 'next/link';
import moment from 'moment';

import {
  SUPPORT_REQUEST_PATH,
  useBillingGate,
} from 'src/util/use-billing-gating';

/**
 * App-wide billing banner with a two-rung severity ladder:
 *   - red "error"  → a hard block is in effect (period ended, or a per-kind
 *     hard cap reached). States what's paused, when it lifts, and carries a
 *     real action — raising a support request, never a bare support link.
 *   - amber "warning" → an enabled kind is approaching its cap. Dismissible for
 *     the session.
 * Renders nothing when nothing is blocked or approaching.
 */
const BillingGateBanner: React.FC = () => {
  const { t } = useTranslation('billing');
  const { usage, blockedKinds, warnKinds, expiredNoRenewal, kindLabel } =
    useBillingGate();
  const [warningDismissed, setWarningDismissed] = useState(false);

  const resetDate = usage ? moment(usage.end_date).format('LL') : '';

  // "company" / "company and UBO" / "company, UBO and compliance check"
  const formatKinds = (kinds: typeof blockedKinds): string => {
    const labels = kinds.map(kindLabel);
    if (labels.length <= 1) return labels.join('');
    return `${labels.slice(0, -1).join(', ')} ${t('and')} ${
      labels[labels.length - 1]
    }`;
  };

  const supportRequest = (
    <Link href={SUPPORT_REQUEST_PATH} style={{ fontWeight: 600 }}>
      {t('Raise a support request')}
    </Link>
  );
  const viewUsage = (
    <Link href="/account/usage" style={{ fontWeight: 600 }}>
      {t('View usage')}
    </Link>
  );

  // Error rung takes precedence over the warning. Check expiredNoRenewal
  // directly so a contract-level expiry always shows, independent of which
  // credit kinds are enabled for the profile.
  if (expiredNoRenewal || blockedKinds.length > 0) {
    const kinds = formatKinds(blockedKinds);
    const message = expiredNoRenewal
      ? t('Your plan has ended — new actions are paused until it is renewed.')
      : blockedKinds.length > 1
        ? t(
            "You've reached your {{kinds}} limits — those actions are paused until {{date}}.",
            { kinds, date: resetDate }
          )
        : t(
            "You've reached your {{kinds}} limit — those actions are paused until {{date}}.",
            { kinds, date: resetDate }
          );

    return (
      <Alert
        type="error"
        banner
        showIcon
        message={message}
        action={supportRequest}
      />
    );
  }

  if (warnKinds.length > 0 && !warningDismissed) {
    const kinds = formatKinds(warnKinds);
    const message =
      warnKinds.length > 1
        ? t("You're approaching your {{kinds}} limits — they reset {{date}}.", {
            kinds,
            date: resetDate,
          })
        : t("You're approaching your {{kinds}} limit — it resets {{date}}.", {
            kinds,
            date: resetDate,
          });
    return (
      <Alert
        type="warning"
        banner
        showIcon
        closable
        onClose={() => setWarningDismissed(true)}
        message={message}
        action={
          <Space size="middle">
            {viewUsage}
            {supportRequest}
          </Space>
        }
      />
    );
  }

  return null;
};

export default BillingGateBanner;
